import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Award,
  Globe,
  TrendingUp,
  LucideIcon,
} from "lucide-react";

const heroImages = [
  "/images/slide 1.jpg", // African professional excellence
  "/images/slide 2.jpg", // African tech innovation
  "/images/slide 3.jpg", // BPO excellence
];

interface CounterState {
  professionals: number;
  clients: number;
  income: number;
  retention: number;
}

const stats: {
  number: string;
  label: string;
  icon: LucideIcon;
  key: keyof CounterState;
  target: number;
  suffix: string;
  prefix?: string;
}[] = [
  {
    number: "1,200+",
    label: "African Professionals",
    icon: Users,
    key: "professionals",
    target: 1200,
    suffix: "+",
  },
  {
    number: "50+",
    label: "Global Clients",
    icon: Globe,
    key: "clients",
    target: 50,
    suffix: "+",
  },
  {
    number: "UGX100m+",
    label: "Income Generated",
    icon: TrendingUp,
    key: "income",
    target: 2.5,
    suffix: "M+",
    prefix: "$",
  },
  {
    number: "95%",
    label: "Client Retention",
    icon: Award,
    key: "retention",
    target: 95,
    suffix: "%",
  },
];

export const HeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [counters, setCounters] = useState<CounterState>({
    professionals: 0,
    clients: 0,
    income: 0,
    retention: 0,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const animateCounters = () => {
      stats.forEach((stat) => {
        const duration = 2000;
        const steps = 60;
        const stepValue = stat.target / steps;
        let currentStep = 0;

        const counter = setInterval(() => {
          currentStep++;
          const value = Math.min(currentStep * stepValue, stat.target);

          setCounters((prev) => ({
            ...prev,
            [stat.key]: value,
          }));

          if (currentStep >= steps) {
            clearInterval(counter);
          }
        }, duration / steps);
      });
    };

    const timer = setTimeout(animateCounters, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left animate-fadeInLeft">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="text-brand-grey">Africa's Bridge</span>
              <br />
              <span className="bg-gradient-to-r from-brand-primary via-brand-tertiary to-brand-primary bg-clip-text text-transparent">
                to Global Markets
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Connecting vetted East African professionals with global
              opportunities. Ready-to-deploy teams in data services, AI, and
              digital marketing.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center lg:justify-start">
              <Link
                to="/contact"
                className="group relative overflow-hidden bg-gradient-to-r from-brand-primary to-brand-tertiary text-white px-8 py-4 rounded-2xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center justify-center">
                  Connect with Our Team
                  <ArrowRight
                    className="ml-2 group-hover:translate-x-1 transition-transform"
                    size={20}
                  />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-brand-tertiary to-brand-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              <Link
                to="/contact"
                className="group border-2 border-brand-primary text-brand-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-brand-secondary/50 transition-all duration-300 transform hover:-translate-y-1"
              >
                See Real Results
              </Link>
            </div>
          </div>

          <div className="mt-8 lg:mt-0 animate-fadeInRight flex items-center justify-center">
            <div className="relative">
              {/* Main Image Container */}
              <div className="relative z-10 bg-white rounded-3xl shadow-xl p-8 transform rotate-3 hover:rotate-0 transition-all duration-500 group overflow-hidden border border-gray-200">
                <img
                  src={heroImages[currentImageIndex]}
                  alt="Professional team collaboration"
                  className="w-full h-80 lg:h-96 object-cover rounded-2xl transition-all duration-500"
                />
                <div className="flex justify-center space-x-2 mt-4">
                  {heroImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex
                          ? "bg-brand-primary"
                          : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section - Full Width */}
        <div className="mt-20 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const StatIcon = stat.icon;

              const formatValue = (value: number) => {
                if (stat.key === "income") {
                  return `$${value.toFixed(1)}M+`;
                } else if (stat.key === "retention") {
                  return `${Math.round(value)}%`;
                } else {
                  return `${Math.round(value)}${stat.suffix}`;
                }
              };

              return (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-r from-brand-primary to-brand-tertiary flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-1 group-hover:scale-110">
                    <StatIcon className="w-8 h-8 text-white" fill="white" />
                  </div>
                  <div className="text-3xl font-bold text-brand-grey mb-2">
                    {formatValue(counters[stat.key as keyof typeof counters])}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
